import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getAPIErrorMessage } from "../../../../helpers/helpers";
import { useDarkMode } from "../../../../hooks/useDarkMode";
import { useSidebarData } from "../../../../hooks/useSidebarData";
import { CompanySpacesService } from "../../../../service/CompanySpacesService";
import { UserService } from "../../../../service/UserService";
import { useAuthStore } from "../../../../store/storeAuth";
import { NotificationType, useNotificationStore } from "../../../../store/storeNotifications";
import { useObjectStore } from "../../../../store/storeObjects";
import { Namespace } from "../../../../types/namespace";
import type { VectorEmbeddingModel } from "../../../../types/vector";
import { executeVectorMemoryDeletion } from "../executeVectorMemoryDeletion";
import { UserObject } from "../../../../util/Types/User";
import ObjectModal from "../../../Layout/LayoutComponents/FluxObjects/ObjectModal";
import { DeleteNamespaceDialog } from "../../../UI/DeleteNamespaceDialog";
import { VectorMemoryModal } from "../../../UI/VectorMemoryModal";
import { FluxObject } from "../AgentsSidebar/context";
import { useExtendedSidebarContext } from "../ExtendedSidebar";
import { CollapsibleFolderGroup } from "./CollapsibleFolderGroup";
import { ObjectItem } from "./ObjectItem";
import { Section } from "./Section";
import { VectorMemoryItem } from "./VectorMemoryItem";
import { DataSidebarContext, DataSidebarContextValue } from "./context";

interface OrganizedItems {
  objects: FluxObject[];
  vectorMemory: Namespace[];
}

interface ModalState {
  objectModal: { show: boolean; editing: FluxObject | null };
  vectorModal: { show: boolean; editing: Namespace | null };
}

type ModalAction =
  | { type: "OPEN_OBJECT"; payload: FluxObject }
  | { type: "CLOSE_OBJECT" }
  | { type: "OPEN_VECTOR"; payload: Namespace }
  | { type: "CLOSE_VECTOR" };

const initialModalState: ModalState = {
  objectModal: { show: false, editing: null },
  vectorModal: { show: false, editing: null },
};

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case "OPEN_OBJECT": return { ...state, objectModal: { show: true, editing: action.payload } };
    case "CLOSE_OBJECT": return { ...state, objectModal: { show: false, editing: null } };
    case "OPEN_VECTOR": return { ...state, vectorModal: { show: true, editing: action.payload } };
    case "CLOSE_VECTOR": return { ...state, vectorModal: { show: false, editing: null } };
  }
}

function filterBySearch(items: OrganizedItems, term: string): OrganizedItems {
  const lower = term.trim().toLowerCase();
  if (!lower) return items;
  return {
    objects: items.objects.filter((o) => o.name?.toLowerCase().includes(lower)),
    vectorMemory: items.vectorMemory.filter((ns) => ns.name?.toLowerCase().includes(lower)),
  };
}

interface ItemsSectionProps {
  items: OrganizedItems;
  onObjectEdit: (obj: FluxObject) => void;
  onObjectDelete: (obj: FluxObject) => void;
  onVectorEdit: (ns: Namespace) => void;
  onVectorDelete: (ns: Namespace) => void;
}

const ItemsSection: React.FC<ItemsSectionProps> = ({
  items,
  onObjectEdit,
  onObjectDelete,
  onVectorEdit,
  onVectorDelete,
}) => (
  <>
    {items.objects.map((obj) => (
      <ObjectItem
        key={obj.id}
        object={obj}
        onEdit={onObjectEdit}
        onDelete={onObjectDelete}
        isNested={false}
      />
    ))}
    {items.vectorMemory.length > 0 && (
      <CollapsibleFolderGroup title="Vector Memory" defaultExpanded>
        {items.vectorMemory.map((ns) => (
          <VectorMemoryItem
            key={ns.id}
            namespace={ns}
            onEdit={onVectorEdit}
            onDelete={onVectorDelete}
            isNested
          />
        ))}
      </CollapsibleFolderGroup>
    )}
  </>
);

export const DataSidebarBody: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();

  const { searchTerm } = useExtendedSidebarContext();
  const { user: authUser, setAuthUser } = useAuthStore((state) => state);
  const { setObjectUpdate, resetObjectUpdate, removeObject, setObjects, objects: objectStore } = useObjectStore((state) => state);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { spaces, user: sidebarUser, isLoading, refresh, updateSpace } = useSidebarData();

  const [activeObjectId, setActiveObjectId] = useState<string | undefined>();
  const [modals, dispatch] = useReducer(modalReducer, initialModalState);
  const [pendingVectorNamespace, setPendingVectorNamespace] = useState<Namespace | null>(null);

  useEffect(() => {
    if (sidebarUser) setAuthUser(sidebarUser);
  }, [sidebarUser, setAuthUser]);

  useEffect(() => {
    window.addEventListener("sidebar-refresh", refresh);
    return () => window.removeEventListener("sidebar-refresh", refresh);
  }, [refresh]);

  const privateItems = useMemo<OrganizedItems>(() => ({
    objects: objectStore["user"] || authUser?.objects || [],
    vectorMemory: (authUser?.namespaces as Namespace[]) || [],
  }), [objectStore, authUser?.objects, authUser?.namespaces]);

  const spaceItems = useMemo(() =>
    spaces.map((space) => ({
      space,
      items: {
        objects: objectStore[space.id]?.length ? objectStore[space.id] : space.objects || [],
        vectorMemory: space.namespaces || [],
      } as OrganizedItems,
    }))
    , [spaces, objectStore]);

  const sortedSpaces = useMemo(() =>
    [...spaceItems].sort((a, b) => Number(!!a.space.disabled) - Number(!!b.space.disabled))
    , [spaceItems]);

  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;
    const all: OrganizedItems = {
      objects: [privateItems.objects, ...spaceItems.map((s) => s.items.objects)].flat(),
      vectorMemory: [privateItems.vectorMemory, ...spaceItems.map((s) => s.items.vectorMemory)].flat(),
    };
    return filterBySearch(all, searchTerm);
  }, [searchTerm, privateItems, spaceItems]);

  const handleObjectEdit = useCallback((obj: FluxObject) => {
    const full = (authUser?.objects ?? []).find((o) => o.id === obj.id)
      ?? spaces.flatMap((s) => s.objects ?? []).find((o) => o.id === obj.id) as UserObject | undefined;
    if (!full) return;
    setObjectUpdate({
      isCreating: false,
      id: full.id,
      name: full.name,
      text: full.text,
      sessions: full.sessions,
      localObject: full.localObject,
      sessionEnabled: full.sessionEnabled,
      flowId: full.flowId,
    });
    dispatch({ type: "OPEN_OBJECT", payload: full });
  }, [authUser?.objects, spaces, setObjectUpdate]);

  const handleObjectDelete = useCallback(async (obj: FluxObject) => {
    if (!window.confirm("Are you sure you want to delete this object?")) return;
    const spaceId = spaces.find((s) => s.objects?.some((o) => o.id === obj.id))?.id;
    removeObject(obj.id, spaceId ?? "user");
    try {
      if (spaceId) {
        const space = spaces.find((s) => s.id === spaceId);
        const updated = (space?.objects ?? []).filter((o) => o.id !== obj.id);
        await CompanySpacesService.updateCompanySpace(authUser?.selectedCompanyId!, { spaceId, objects: updated });
        setObjects(updated as UserObject[], spaceId);
      } else {
        const updated = (authUser?.objects ?? []).filter((o) => o.id !== obj.id);
        await UserService.updateUser({ objects: updated });
        setObjects(updated, "user");
        refresh();
      }
      setNotification({ type: NotificationType.Success, message: "Object deleted successfully" });
    } catch (error) {
      refresh();
      setNotification({ type: NotificationType.Error, message: getAPIErrorMessage(error as any) });
    }
  }, [authUser, spaces, removeObject, setObjects, refresh, setNotification]);

  const requestVectorDelete = useCallback((ns: Namespace) => {
    setPendingVectorNamespace(ns);
  }, []);

  const handleEditSpace = useCallback((spaceId: string) => {
    navigate(`${location.pathname}?space=true&spaceId=${spaceId}`);
    window.dispatchEvent(new CustomEvent("open-settings-modal"));
  }, [navigate, location.pathname]);

  const handleArchiveSpace = useCallback(async (spaceId: string) => {
    if (!window.confirm("Are you sure you want to archive this space?")) return;
    try {
      await CompanySpacesService.disableSpace(spaceId);
      refresh();
      setNotification({ type: NotificationType.Success, message: "Space archived successfully" });
    } catch (error) {
      setNotification({ type: NotificationType.Error, message: getAPIErrorMessage(error as any) });
    }
  }, [refresh, setNotification]);

  if (isLoading && spaces.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "24px" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const { objectModal, vectorModal } = modals;

  const contextValue: DataSidebarContextValue = {
    isDarkMode,
    palette,
    activeObjectId,
    onObjectClick: (obj) => setActiveObjectId(obj.id),
  };

  const itemHandlers: Omit<ItemsSectionProps, "items"> = {
    onObjectEdit: handleObjectEdit,
    onObjectDelete: handleObjectDelete,
    onVectorEdit: (ns) => dispatch({ type: "OPEN_VECTOR", payload: ns }),
    onVectorDelete: requestVectorDelete,
  };

  return (
    <DataSidebarContext.Provider value={contextValue}>
      {objectModal.show && (
        <ObjectModal
          show
          onHide={async () => { dispatch({ type: "CLOSE_OBJECT" }); resetObjectUpdate(); refresh(); }}
          spaceId={spaces.find((s) => s.objects?.some((o) => o.id === objectModal.editing?.id))?.id ?? "user"}
        />
      )}

      {vectorModal.show && (
        <VectorMemoryModal
          open
          namespaceId={vectorModal.editing?.id}
          spaceId={spaces.find((s) => s.namespaces?.some((n) => n.id === vectorModal.editing?.id))?.id}
          onOpenChange={async ({ open }) => { if (!open) { dispatch({ type: "CLOSE_VECTOR" }); refresh(); } }}
        />
      )}

      {pendingVectorNamespace ? (
        <DeleteNamespaceDialog
          open
          embeddingModelName={
            (pendingVectorNamespace.embeddingModelName ||
              "voyage-3-large") as VectorEmbeddingModel
          }
          id={pendingVectorNamespace.id}
          name={pendingVectorNamespace.name}
          onOpenChange={(open) => {
            if (!open) setPendingVectorNamespace(null);
          }}
          onNamespaceDelete={({ embeddingModelName, namespaceId, namespaceName }) => {
            void executeVectorMemoryDeletion(
              {
                id: namespaceId,
                name: namespaceName,
                embeddingModelName,
              } as Namespace,
              {
                spaces,
                updateSpace,
                refreshSidebar: refresh,
                setNotification,
              },
            ).then(() => setPendingVectorNamespace(null));
          }}
        />
      ) : null}

      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {searchResults ? (
          <ItemsSection items={searchResults} {...itemHandlers} />
        ) : (
          <>
            <Section title="Private" hasBorder={spaces.length > 0} isAccordion defaultExpanded>
              <ItemsSection items={privateItems} {...itemHandlers} />
            </Section>

            {sortedSpaces.map(({ space, items }, index) => (
              <Section
                key={space.id}
                title={space.name}
                hasBorder={index < sortedSpaces.length - 1}
                isAccordion
                defaultExpanded={false}
                isDisabled={!!space.disabled}
                onEditSpace={() => handleEditSpace(space.id)}
                onArchive={() => handleArchiveSpace(space.id)}
              >
                {items.objects.length === 0 && items.vectorMemory.length === 0 && (
                  <Typography sx={{ color: isDarkMode ? palette.gray[500] : palette.gray[400], fontFamily: "Roboto", fontSize: "12px", fontStyle: "italic", padding: "8px" }}>
                    No items found
                  </Typography>
                )}
                <ItemsSection items={items} {...itemHandlers} />
              </Section>
            ))}
          </>
        )}
      </Box>
    </DataSidebarContext.Provider>
  );
};
